import clsx from 'clsx';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean | undefined;
  children: ReactNode;
}

const base =
  'inline-flex items-center justify-center gap-2 rounded-[var(--radius-md)] font-medium ' +
  'transition-[transform,box-shadow,background,color,border-color] duration-[var(--duration-fast)] ' +
  'active:translate-y-px disabled:active:translate-y-0';

const sizes: Record<Size, string> = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
};

const variants: Record<Variant, string> = {
  primary:
    'text-[#102000] border-0 ' +
    'bg-[linear-gradient(180deg,var(--color-accent-hover),var(--color-accent))] ' +
    'shadow-[inset_0_1px_0_rgb(255_255_255_/_0.4),0_1px_2px_rgb(0_0_0_/_0.25),var(--shadow-accent)] ' +
    'hover:-translate-y-px hover:shadow-[inset_0_1px_0_rgb(255_255_255_/_0.5),0_4px_8px_rgb(0_0_0_/_0.3),0_14px_36px_-10px_var(--color-accent-glow)]',
  secondary:
    'text-text-primary border border-border-strong ' +
    'bg-[linear-gradient(180deg,var(--color-bg-overlay),var(--color-bg-raised))] ' +
    'shadow-[inset_0_1px_0_rgb(255_255_255_/_0.06),var(--shadow-1)] ' +
    'hover:border-text-muted hover:shadow-[inset_0_1px_0_rgb(255_255_255_/_0.10),var(--shadow-2)]',
  ghost:
    'text-text-secondary bg-transparent border-0 hover:text-text-primary hover:bg-bg-raised/40',
  danger:
    'text-white border-0 ' +
    'bg-[linear-gradient(180deg,#ff8e72,var(--color-danger))] ' +
    'shadow-[inset_0_1px_0_rgb(255_255_255_/_0.3),0_4px_14px_-6px_rgb(255_122_89_/_0.55)] ' +
    'hover:-translate-y-px hover:shadow-[inset_0_1px_0_rgb(255_255_255_/_0.4),0_6px_18px_-6px_rgb(255_122_89_/_0.7)]',
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading,
  children,
  className,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={clsx(base, sizes[size], variants[variant], className)}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-label="loading" /> : children}
    </button>
  );
}
